/**
 * Turns API failures into friendly, translated text for the auth screens, the avatar editor
 * and anything else that wants the same wording (incl. the 429 "wait N minutes" message).
 */
import { ERROR_CODES, type ErrorCode } from '../../../shared/errors';
import { ApiFailure, isNetworkFailure, serverNow } from '../../api/client';

/**
 * The subset of message keys used here. Kept local (instead of importing the i18n module) so this
 * file stays React-free for unit tests; the app's `t` is assignable because these keys exist.
 */
type ErrorTextKey = 'auth.wait_seconds' | 'auth.wait_minutes' | 'error.network' | 'common.error' | `error.${ErrorCode}`;
type T = (key: ErrorTextKey, vars?: Record<string, string | number>) => string;

const KNOWN = new Set<string>(ERROR_CODES);

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Seconds until another attempt is allowed, read from the 429 error details.
 * Accepts { retryAfter } / { retryAfterSec } (seconds), { retryAfterMs } or { nextAllowedAt } (epoch ms).
 */
export function retryAfterSeconds(details: unknown, now: number = serverNow()): number | null {
  if (!details || typeof details !== 'object') return null;
  const d = details as Record<string, unknown>;
  let s: number | null = num(d.retryAfter) ?? num(d.retryAfterSec);
  if (s === null) {
    const ms = num(d.retryAfterMs);
    if (ms !== null) s = ms / 1000;
  }
  if (s === null) {
    const at = num(d.nextAllowedAt);
    if (at !== null) s = (at - now) / 1000;
  }
  if (s === null || s <= 0) return null;
  return Math.ceil(s);
}

export function errorCodeOf(e: unknown): ErrorCode | 'network' | null {
  if (isNetworkFailure(e)) return 'network';
  if (e instanceof ApiFailure && KNOWN.has(e.code)) return e.code;
  return null;
}

/** Translated message for any error thrown by api()/apiRaw() or the auth helpers. */
export function apiErrorText(t: T, e: unknown): string {
  if (isNetworkFailure(e)) return t('error.network');
  if (e instanceof ApiFailure) {
    if (e.code === 'too_many_requests' || e.status === 429) {
      const s = retryAfterSeconds(e.details);
      if (s !== null) return s < 60 ? t('auth.wait_seconds', { count: s }) : t('auth.wait_minutes', { count: Math.ceil(s / 60) });
      return t('error.too_many_requests');
    }
    if (KNOWN.has(e.code)) return t(`error.${e.code}`);
  }
  return t('common.error');
}
